import serialize from 'serialize-javascript';

import type {
  CountryCodesAlpha2,
  TemplateData,
} from '@xing-com/crate-common-graphql-types';
import { JobRemoteOptionValue } from '@xing-com/crate-common-graphql-types';
import { Helmet } from '@xing-com/crate-helmet';
import type { DescriptionFragment, Salary } from '@xing-com/crate-jobs-graphql';
import type { JobDetails } from '@xing-com/crate-jobs-helpers';

type Props = {
  job: JobDetails;
};

const isDefined = <T,>(value: T | null | undefined): value is T =>
  value !== null && value !== undefined;

const concatenateTemplateData = (description: TemplateData): string => `
    ${description.genericDescription}
    ${description.companyDescriptionTitle}
    ${description.companyDescriptionContent}
    ${description.responsibilityTitle}
    ${description.responsibilityContent}
    ${description.skillsTitle}
    ${description.skillsContent}
    ${description.weOfferTitle}
    ${description.weOfferContent}
    ${description.contactInfoTitle}
    ${description.contactInfoContent}
  `;

const getJobDescription = (description: DescriptionFragment): string => {
  switch (description.__typename) {
    case 'HtmlDescription':
      return description.content ?? '';
    case 'TemplateData':
      return concatenateTemplateData(description);
    case 'ExternalUrlDescription':
      return description.htmlContent;
    default:
      return '';
  }
};

const getJobSalary = (salary?: Salary): object | undefined => {
  if (salary?.__typename === 'Salary') {
    const { currency, amount } = salary;

    return isDefined(currency) && isDefined(amount)
      ? {
          '@type': 'MonetaryAmount',
          currency,
          value: {
            '@type': 'QuantitativeValue',
            value: amount,
            unitText: 'YEAR',
          },
        }
      : undefined;
  }
  if (salary?.__typename === 'SalaryRange') {
    const { currency, minimum, maximum } = salary;
    return isDefined(currency) && (isDefined(minimum) || isDefined(maximum))
      ? {
          '@type': 'MonetaryAmount',
          currency,
          value: {
            '@type': 'QuantitativeValue',
            minValue: minimum ?? undefined,
            maxValue: maximum ?? undefined,
            unitText: 'YEAR',
          },
        }
      : undefined;
  }
  return undefined;
};

const getUniqueCountries = (
  locations: JobDetails['locations']
): { '@type': string; name: CountryCodesAlpha2 }[] => {
  return [
    ...new Set(
      locations
        .map((location) => location.country?.countryCode)
        .filter((code) => !!code)
    ),
  ].map((countryCode) => ({
    '@type': 'Country',
    name: countryCode,
  }));
};

export const StructuredData: React.FC<Props> = ({ job }) => {
  const {
    activatedAt,
    activeUntil,
    application,
    company,
    description,
    facts: { employmentType },
    highlights,
    industry,
    locations,
    salary,
    title,
    url,
  } = job;

  const hiringOrganization = {
    '@type': 'Organization',
    name: company?.name,
    url: company?.link,
    logo: company?.logo,
  };

  const jobLocations = locations.map((location) => ({
    '@type': 'Place',
    address: {
      '@type': 'PostalAddress',
      streetAddress: location.street,
      addressLocality: location.city,
      addressRegion: location.region,
      postalCode: location.zipCode,
      addressCountry: location.country?.countryCode,
    },
  }));

  const directApply =
    application.type === 'JobXingApplication' ||
    application.type === 'JobXingCustomApplication';

  // Using https://developers.google.com/search/docs/data-types/job-posting#structured-data-type-definitions
  const jobPostingSchema = {
    '@context': 'https://schema.org/',
    '@type': 'JobPosting',
    title,
    description: getJobDescription(description),
    datePosted: activatedAt,
    directApply,
    url,
    validThrough: activeUntil,
    industry: industry?.name,
    employmentType: employmentType?.name,
    hiringOrganization,
    baseSalary: getJobSalary(salary),
    jobLocation: jobLocations,
    ...(highlights.remoteOption === JobRemoteOptionValue.FullRemote
      ? {
          jobLocationType: 'TELECOMMUTE',
          applicantLocationRequirements: getUniqueCountries(locations),
        }
      : {}),
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {serialize(jobPostingSchema, { isJSON: true })}
      </script>
    </Helmet>
  );
};
